import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import UpdateTimerNodePreview from "../../features/sequence/node_previews/UpdateTimerNodePreview";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { Actor } from "../Actor";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";

export enum TimerFunction {
  Start = 0,
  Stop = 1,
  Reset = 2,
  SetTime = 3,
  AddTime = 4,
  Destroy = 5,
  Show = 6,
  Hide = 7,
  SetSpeed = 8,
  Loop = 9,
  ScaledTime = 10,
}

export interface IUpdateTimer extends Interaction {
  function: TimerFunction;
  value: number;
}

export function isParametricTimerFunction(fn: TimerFunction): boolean {
  return (
    fn === TimerFunction.SetTime ||
    fn === TimerFunction.AddTime ||
    fn === TimerFunction.SetSpeed ||
    fn === TimerFunction.Loop ||
    fn === TimerFunction.ScaledTime
  );
}

export function getTimerFunctionLabel(fn: TimerFunction): string {
  switch (fn) {
    case TimerFunction.Start:
      return "Start";
    case TimerFunction.Stop:
      return "Stop";
    case TimerFunction.Reset:
      return "Reset";
    case TimerFunction.SetTime:
      return "SetTime";
    case TimerFunction.AddTime:
      return "AddTime";
    case TimerFunction.Destroy:
      return "Destroy";
    case TimerFunction.Show:
      return "Show";
    case TimerFunction.Hide:
      return "Hide";
    case TimerFunction.SetSpeed:
      return "SetSpeed";
    case TimerFunction.Loop:
      return "Loop";
    case TimerFunction.ScaledTime:
      return "ScaledTime";
    default:
      return "Unknown";
  }
}

export function getUpdateTimerNodePreview(timer: IUpdateTimer): string {
  const fnLabel = getTimerFunctionLabel(timer.function);
  if (isParametricTimerFunction(timer.function)) {
    return `Update Timer: ${fnLabel}: ${timer.value}`;
  }
  return `Update Timer: ${fnLabel}`;
}

export const updateTimerInteraction = (): InteractionFactoryEntity => {
  const create = (getKeys: (amount: number) => string[]): IUpdateTimer => {
    return {
      id: GetNewGuid(),
      name: "Update Timer",
      type: InteractionType.UpdateTimer,
      function: TimerFunction.Start,
      value: 0,
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction
  ): IUpdateTimer => {
    return interaction as IUpdateTimer;
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[]
  ): IUpdateTimer => {
    const casted = interaction as IUpdateTimer;
    return {
      ...casted,
      id: GetNewGuid(),
      name: "Update Timer",
      type: InteractionType.UpdateTimer,
    };
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string
  ): string[] => {
    return [];
  };

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[]
  ): string[] => {
    const casted = interaction as IUpdateTimer;
    const errors: string[] = [];

    if (
      isParametricTimerFunction(casted.function) &&
      !Number.isFinite(casted.value)
    ) {
      errors.push("Value must be a valid number for this function");
    }

    return errors;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[]
  ): SearchResultData[] => {
    const casted = interaction as IUpdateTimer;
    return getMatchingResults(
      [interaction.name, getTimerFunctionLabel(casted.function)],
      takeName,
      interaction,
      "Update Timer"
    );
  };

  return {
    View: <></>,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <UpdateTimerNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    FilterType: "Update Timer",
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
